import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import CognitoLoginForm from '@/components/commons/Customer/CognitoLoginForm';
import { CustomerFormType } from '@/components/commons/Customer/types';
import Loading from '@/components/Loading/Loading';
import { clearAuthReturnTo, setAuthReturnTo } from '@/lib/customer/auth-return';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import i18n from '@/settings/i18n';
import BusinessNameForm from './BusinessNameForm';
import ErrorState from './ErrorState';
import ExemptaxForm from './ExemptaxForm';
import SignupForm from './SignupForm';

const t = i18n.init('business');

const Card = ({
  children,
  wide = false,
}: {
  children: React.ReactNode;
  wide?: boolean;
}) => (
  <div
    className={classNames(
      'mx-auto w-full rounded-xl border bg-white p-5 shadow-[0_2px_12px_rgba(0,0,0,0.04)] md:p-6',
      wide ? 'max-w-[560px]' : 'max-w-[420px]',
    )}
  >
    {children}
  </div>
);

/**
 * The page's account card, by the customer's state:
 * - signed out: log in or create a business account;
 * - signed in without a business name: "One more thing";
 * - with one, and their Stripe customer: the Exemptax certificate form;
 * - with one but no Stripe customer: the set-up error.
 */
const TaxExemptAccount = () => {
  const router = useRouter();
  const { customer, loading } = useCustomerContext();
  const [tab, setTab] = useState<CustomerFormType>(CustomerFormType.LOGIN);
  // While a sign-up runs, the sign-in it ends with must not swap the form out.
  const [signingUp, setSigningUp] = useState(false);
  const [unsavedBusinessName, setUnsavedBusinessName] = useState<string>();
  // Only the first load waits on the customer. Later, `loading` is a form's
  // own request (sign-in, sign-up), and swapping the form for a spinner then
  // would unmount it mid-flow and reset it.
  const [ready, setReady] = useState(!loading);

  useEffect(() => {
    if (!loading) {
      setReady(true);
    }
  }, [loading]);

  // A Google sign-in leaves the site; come back here afterwards. Leaving any
  // other way (in-app navigation, or signing in here) forgets it, so a later
  // Google sign-in elsewhere doesn't land on this page.
  useEffect(() => {
    if (customer) {
      return undefined;
    }

    setAuthReturnTo(router.asPath);

    return clearAuthReturnTo;
  }, [customer, router.asPath]);

  if (!ready && !customer) {
    return (
      <Card>
        <div className="flex min-h-[200px] items-center justify-center">
          <Loading />
        </div>
      </Card>
    );
  }

  if (!customer || signingUp) {
    return (
      <Card>
        <h2 className="font-medium">{t`Get started`}</h2>
        <p className="mt-1 text-xs text-gray-700/60">
          {t`Sign in or create your Reebelo Business account.`}
        </p>
        <div
          role="tablist"
          aria-label={t`Get started`}
          className="mt-4 grid grid-cols-2 gap-1 rounded-md bg-gray-200 p-1 text-xs"
        >
          {[
            { value: CustomerFormType.LOGIN, label: t`Log in` },
            { value: CustomerFormType.REGISTER, label: t`Sign up` },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              disabled={signingUp}
              onClick={() => setTab(value)}
              className={classNames(
                'h-8 rounded font-medium transition-colors',
                tab === value
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-700/70 hover:text-gray-700',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="mt-4" role="tabpanel">
          {tab === CustomerFormType.LOGIN ? (
            <CognitoLoginForm
              showHeader={false}
              setFormType={setTab}
              // Signing in updates the customer, which moves the card on.
              onSuccess={() => undefined}
            />
          ) : (
            <SignupForm
              onStart={() => setSigningUp(true)}
              onDone={(name) => {
                setUnsavedBusinessName(name);
                setSigningUp(false);
              }}
            />
          )}
        </div>
      </Card>
    );
  }

  if (!customer.businessName) {
    return (
      <Card>
        <BusinessNameForm initialValue={unsavedBusinessName} />
      </Card>
    );
  }

  if (!customer.stripeCustomerId) {
    return (
      <Card>
        <ErrorState />
      </Card>
    );
  }

  return (
    <Card wide>
      <ExemptaxForm />
    </Card>
  );
};

export default TaxExemptAccount;
